import styled from '@emotion/styled';

// Pendo click tracking targets this class name; renaming it silently breaks
// that dashboard configuration.
export const PORTFOLIO_LOGO_LINK_CLASS = 'rwl-portfolio-logo-link';

const Logo = styled.img`
    box-sizing: border-box;
    height: var(--rwl-header-height);
    align-self: stretch;
    object-fit: contain;
    padding: var(--rwl-spacing-sm) var(--rwl-spacing-md);
    max-width: 160px;
`;

const LogoLink = styled.a`
    display: flex;
    align-items: stretch;
`;

/**
 * Builds the logo click-through URL: the account website tagged with UTM
 * params so the visit is attributed to us in the business's own analytics.
 * Returns undefined when there is no usable URL; callers should render the
 * unlinked logo in that case.
 */
export const buildLogoWebsiteUrl = (website?: string): string | undefined => {
    if (!website) {
        return undefined;
    }
    const withProtocol = /^https?:\/\//i.test(website)
        ? website
        : `https://${website}`;
    try {
        const url = new URL(withProtocol);
        // Junk values like "N/A" or "pending" parse as single-label hosts
        // once https:// is prepended; a real business website always has a
        // dot in its hostname. Better an unlinked logo than a broken link.
        if (!url.hostname.includes('.')) {
            warnUnusableWebsite(website);
            return undefined;
        }
        url.searchParams.set('utm_source', 'realworklabs');
        url.searchParams.set('utm_medium', 'portfolio');
        url.searchParams.set('utm_content', 'logo');
        return url.toString();
    } catch {
        warnUnusableWebsite(website);
        return undefined;
    }
};

// console.warn, not Sentry: a persistent bad website on one account would
// otherwise emit an event per portfolio visit.
const warnUnusableWebsite = (website: string) => {
    console.warn(
        'Account website is not a usable URL; rendering unlinked logo',
        website
    );
};

interface HeaderLogoProps {
    url?: string;
    name: string;
    website?: string;
}

export const HeaderLogo = ({ url, name, website }: HeaderLogoProps) => {
    if (!url) {
        return null;
    }
    const logo = <Logo src={url} alt="logo" aria-details={`Logo of ${name}`} />;

    const href = buildLogoWebsiteUrl(website);
    if (!href) {
        return logo;
    }
    return (
        <LogoLink
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={PORTFOLIO_LOGO_LINK_CLASS}
            aria-label={`Visit the ${name} website`}
        >
            {logo}
        </LogoLink>
    );
};
